import { useContext, useEffect, useState, useCallback } from 'react';
import { CircularProgress, MenuItem } from '@mui/material';
// Our Componentes
import { ActivationModal } from './ActivationModal';
import { AuthContext } from 'context/auth-context';
import { CardContext } from 'context/cards/cardContext';
import { Cards } from 'lib/api';
import { GET_CARDS, SET_CARD, OPEN_EDIT_CARD_FORM_MODAL, OPEN_EDIT_CARD_FUNDS_MODAL } from 'context/cards/types';
import ConfirmationModal from 'components/shared/ConfirmationModal';
import ReplenishModal from './ReplenishModal';
// Utils and Helpers
import { hasCreditModel, hasPrepaidModel } from 'helpers/businesses';
import { SOLIDFI_CARD_STATUS } from 'helpers/constants';

const ACTION = {
    CANCEL: 'cancel',
    FREEZE: 'freeze',
    UNFREEZE: 'unfreeze',
    ACTIVATE: 'activate',
    REPLENISH: 'replenish'
};

const MENU_LABELS = {
    ACTIVATE: 'Activate',
    ADD_FUNDS: 'Add Funds',
    CANCEL: 'Cancel',
    EDIT: 'Edit',
    FREEZE: 'Freeze',
    REPLENISH: 'Replenish',
    UNFREEZE: 'Unfreeze'
};

const CardMenuItem = ({
    cardId,
    card,
    isDeactivated = false,
    edit = false,
    editFunds = false,
    cancelCard = false,
    cardStatus = '',
    enableReplenish = false,
    closeMenu = () => {},
    setIsAlertDialogOpen = () => {}
}) => {
    const { businessInfo } = useContext(AuthContext);
    const { dispatch, cards, statistics } = useContext(CardContext);
    const [update, setUpdate] = useState(false);
    const [isActivateModalOpen, setIsActivateModalOpen] = useState(false);
    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
    const [isReplenishModalOpen, setIsReplenishModalOpen] = useState(false);
    const [action, setAction] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [actionLoading, setActionLoading] = useState(false);

    const doCancelCard = useCallback(async () => {
        try {
            await Cards.cancelCard(cardId);
            dispatch({
                type: GET_CARDS
            });
            setIsConfirmationModalOpen(false);
            setUpdate(false);
        } catch (error) {
            setErrorMessage('Action denied');
            setUpdate(false);
        }
    }, [cardId, dispatch]);
    const updateStatusCard = useCallback(async () => {
        try {
            await Cards.updateStatusCard({ id: cardId, isDeactivated });
            dispatch({
                type: GET_CARDS
            });
            setIsConfirmationModalOpen(false);
            setUpdate(false);
        } catch (error) {
            setErrorMessage(error?.response?.data?.message);
            setUpdate(false);
        }
        setActionLoading(false);
    }, [cardId, dispatch, isDeactivated]);
    const fetchCard = useCallback(async () => {
        setActionLoading(true);
        try {
            const cardData = await Cards.getCard(cardId);
            dispatch({
                type: SET_CARD,
                payload: cardData
            });
            if (edit && hasCreditModel(businessInfo)) {
                dispatch({ type: OPEN_EDIT_CARD_FORM_MODAL });
            }
            if ((edit || editFunds) && hasPrepaidModel(businessInfo)) {
                dispatch({ type: OPEN_EDIT_CARD_FUNDS_MODAL });
            }
        } catch (error) {
            setErrorMessage(error?.response?.data?.message);
        }
        setUpdate(false);
        setActionLoading(false);
    }, [businessInfo, cardId, dispatch, edit, editFunds]);
    const replenishCardFunds = useCallback(async () => {
        setActionLoading(true);
        try {
            await Cards.replenishFunds(cardId, {
                amount: Number(card?.limitAmount) - Number(card?.balance)
            });
            dispatch({ type: GET_CARDS });
        } catch (error) {
            console.error(error);
        }
        setActionLoading(false);
    }, [card?.balance, card?.limitAmount, cardId, dispatch]);
    useEffect(() => {
        if (update) {
            if (cancelCard) {
                doCancelCard();
                return;
            }
            if (enableReplenish) {
                replenishCardFunds();
                return;
            }
            if (edit || editFunds) {
                fetchCard();
            } else {
                updateStatusCard();
            }
        }
    }, [
        cancelCard,
        cards,
        doCancelCard,
        edit,
        editFunds,
        enableReplenish,
        fetchCard,
        replenishCardFunds,
        update,
        updateStatusCard
    ]);

    const handleActionClick = () => {
        if (actionLoading) return false;
        if (edit || editFunds) {
            closeMenu();
            if (editFunds && statistics.availableToAddtoCards <= 0) {
                return setIsAlertDialogOpen(true);
            }
            return setUpdate(true);
        }
        if (isDeactivated || (!isDeactivated && cardStatus === SOLIDFI_CARD_STATUS.INACTIVE)) setAction(ACTION.UNFREEZE);
        if (!isDeactivated && cardStatus === SOLIDFI_CARD_STATUS.ACTIVE) setAction(ACTION.FREEZE);
        if (!isDeactivated && cardStatus === SOLIDFI_CARD_STATUS.PENDING_ACTIVATION) {
            setIsActivateModalOpen(true);
            return;
        }
        if (enableReplenish) {
            setAction(ACTION.REPLENISH);
            return setUpdate(true);
        }
        if (cancelCard) setAction(ACTION.CANCEL);

        setIsConfirmationModalOpen(true);
    };

    const getMenuLabel = () => {
        if (actionLoading) {
            return <CircularProgress size="1em" />;
        }
        if (!isDeactivated && cardStatus === SOLIDFI_CARD_STATUS.ACTIVE) return MENU_LABELS.FREEZE;
        if (!isDeactivated && cardStatus === SOLIDFI_CARD_STATUS.PENDING_ACTIVATION) return MENU_LABELS.ACTIVATE;
        if (cancelCard) return MENU_LABELS.CANCEL;
        if (edit) return MENU_LABELS.EDIT;
        if (editFunds) return MENU_LABELS.ADD_FUNDS;
        if (enableReplenish) return MENU_LABELS.REPLENISH;
        if (isDeactivated || (!isDeactivated && cardStatus === SOLIDFI_CARD_STATUS.INACTIVE)) return MENU_LABELS.UNFREEZE;
    };

    return (
        <>
            <MenuItem onClick={handleActionClick}>{getMenuLabel()}</MenuItem>
            <ConfirmationModal
                open={isConfirmationModalOpen}
                close={() => setIsConfirmationModalOpen(!isConfirmationModalOpen)}
                action={action}
                loader={update}
                nextFuntion={() => setUpdate(true)}
                errorMessage={errorMessage}
                component={'card'}
            />
            <ActivationModal id={cardId} open={isActivateModalOpen} setOpen={setIsActivateModalOpen} />
            <ReplenishModal cardId={cardId} card={card} open={isReplenishModalOpen} setOpen={setIsReplenishModalOpen} />
        </>
    );
};

export default CardMenuItem;
